/**
 * Environment validation utilities for SaaS Factory Payments
 */

export interface EnvironmentConfig {
  stripePublishableKey: string;
  stripeSecretKey?: string;
  stripeWebhookSecret?: string;
  environment: 'development' | 'production';
  appUrl?: string;
}

export const validateEnvironment = (
  config: Partial<EnvironmentConfig>
): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required fields
  if (!config.stripePublishableKey) {
    errors.push('STRIPE_PUBLISHABLE_KEY is required');
  } else if (!config.stripePublishableKey.startsWith('pk_')) {
    errors.push('STRIPE_PUBLISHABLE_KEY must start with "pk_"');
  }

  if (!config.environment) {
    errors.push('Environment must be specified (development or production)');
  } else if (!['development', 'production'].includes(config.environment)) {
    errors.push('Environment must be either "development" or "production"');
  }

  // Environment-specific validation
  if (config.environment === 'production') {
    if (config.stripePublishableKey?.includes('test')) {
      warnings.push('Using test keys in production environment');
    }

    if (!config.appUrl) {
      warnings.push('APP_URL should be set in production for proper redirects');
    } else if (!config.appUrl.startsWith('https://')) {
      warnings.push('APP_URL should use HTTPS in production');
    }
  }

  // Server-side validation
  if (typeof window === 'undefined') {
    if (!config.stripeSecretKey) {
      warnings.push(
        'STRIPE_SECRET_KEY is recommended for server-side operations'
      );
    } else if (!config.stripeSecretKey.startsWith('sk_')) {
      errors.push('STRIPE_SECRET_KEY must start with "sk_"');
    }

    if (!config.stripeWebhookSecret) {
      warnings.push(
        'STRIPE_WEBHOOK_SECRET is recommended for webhook verification'
      );
    } else if (!config.stripeWebhookSecret.startsWith('whsec_')) {
      errors.push('STRIPE_WEBHOOK_SECRET must start with "whsec_"');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export const getEnvironmentConfig = (): EnvironmentConfig => {
  const isServer = typeof window === 'undefined';

  // Client-side configuration
  const config: EnvironmentConfig = {
    stripePublishableKey:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ||
      process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ||
      '',
    environment:
      (process.env.NODE_ENV as 'development' | 'production') || 'development',
    appUrl: process.env.NEXT_PUBLIC_APP_URL,
  };

  // Server-side configuration
  if (isServer) {
    config.stripeSecretKey = process.env.STRIPE_SECRET_KEY;
    config.stripeWebhookSecret = process.env.STRIPE_WEBHOOK_SECRET;
  }

  return config;
};

export const validateCurrentEnvironment = () => {
  const config = getEnvironmentConfig();
  const validation = validateEnvironment(config);

  if (!validation.isValid) {
    console.error('❌ Environment validation failed:');
    validation.errors.forEach(error => console.error(`  - ${error}`));

    if (typeof window === 'undefined') {
      // Server-side: throw error
      throw new Error(
        `Environment validation failed: ${validation.errors.join(', ')}`
      );
    }
  }

  if (validation.warnings.length > 0) {
    console.warn('⚠️ Environment warnings:');
    validation.warnings.forEach(warning => console.warn(`  - ${warning}`));
  }

  return validation;
};

// Helper to check if we're in development mode
export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development';
};

// Helper to check if we're in production mode
export const isProduction = () => {
  return process.env.NODE_ENV === 'production';
};

// Helper to check if we're on the server
export const isServer = () => {
  return typeof window === 'undefined';
};

// Helper to check if we're on the client
export const isClient = () => {
  return typeof window !== 'undefined';
};
